import React, { useState } from "react";
import { Input } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import { DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import { PageHeader } from "../../components";
import { Production, RawMaterialAction } from "../../Types";
import { useProductionData } from "./common/useProduction";
import { useSectionAccess } from "../../Services";
import { ProductionTable } from "./common/ProductionTable";
import { ProductionDetailDrawer } from "./common/ProductionDetailDrawer";
import { NewProductionDrawer } from "./common/NewProductionDrawer";

export const ProductionPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);

  const [newDrawerOpen, setNewDrawerOpen] = useState(false);
  const [selectedProduction, setSelectedProduction] =
    useState<Production | null>(null);

  const { productions, total, isLoading, refetch } = useProductionData(
    page,
    search,
  );

  const { isAllowed } = useSectionAccess("raw_materials");

  return (
    <div className="p-4 sm:p-6 space-y-4 bg-gray-50 min-h-screen">
      <PageHeader
        title="Production"
        subText="Convert raw materials into finished product stock."
        handleButtons={[
          ...(isAllowed(RawMaterialAction.ManageProduction)
            ? [
                {
                  icon: PlusOutlined,
                  onClick: () => setNewDrawerOpen(true),
                  title: "New Production",
                },
              ]
            : []),
          { icon: ReloadOutlined, onClick: refetch, title: "Refresh" },
        ]}
      />

      <div className="flex items-center gap-2">
        <Input.Search
          placeholder="Search product, SKU, batch…"
          allowClear
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setPage(1);
          }}
          size="middle"
          style={{ maxWidth: 360 }}
          className="shadow-sm rounded-md"
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <ProductionTable
          data={productions}
          total={total}
          loading={isLoading}
          page={page}
          onPageChange={setPage}
          onViewDetail={(r) => setSelectedProduction(r)}
        />
      </div>

      <NewProductionDrawer
        open={newDrawerOpen}
        onClose={() => setNewDrawerOpen(false)}
      />

      <ProductionDetailDrawer
        production={selectedProduction}
        open={!!selectedProduction}
        onClose={() => setSelectedProduction(null)}
      />
    </div>
  );
};
